import { Badge } from "@/components/ui/Badge";
import { Bot } from "lucide-react";
import { DetailHero } from "@/components/detail-hero";
import { Page } from "@/components/page-layout";
import { RequireScope } from "@/components/require-scope";
import { Heading } from "@/components/ui/Heading";
import {
  PageTabsTrigger,
  Tabs,
  TabsContent,
  PageTabsList,
} from "@/components/ui/Tabs";
import { Text } from "@/components/ui/Text";
import { useRBAC } from "@/hooks/useRBAC";
import { useRoutes } from "@/routes";
import {
  invalidateAllOrganizationRemoteSessionClient,
  useOrganizationRemoteSessionClient,
} from "@gram/client/react-query/organizationRemoteSessionClient.js";
import { useOrganizationRemoteSessionIssuer } from "@gram/client/react-query/organizationRemoteSessionIssuer.js";
import { useUpdateOrganizationRemoteSessionClientMutation } from "@gram/client/react-query/updateOrganizationRemoteSessionClient.js";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link, Navigate, useLocation, useParams } from "react-router";
import { ScopeBadge } from "@/lib/remote-identity";
import { LegacyCallbackAlert } from "./clientAlerts";
import { remoteSessionClientDisplayName } from "./clientDisplay";
import { issuerDisplayName } from "./issuerDisplay";
import { OverviewTab } from "./tabs/client/OverviewTab";
import { McpServersTab } from "./tabs/client/McpServersTab";
import { SessionsTab } from "./tabs/client/SessionsTab";
import { activeDetailTab } from "@/lib/detail-tabs";
import { CLIENT_TABS, type ClientTab } from "./tabs";

// Maps a client tab value to its route subpage key (the MCP Servers tab's URL
// segment is "mcp-servers" but its route key is camelCase "mcpServers").
const CLIENT_TAB_ROUTE_KEY: Record<
  ClientTab,
  "overview" | "mcpServers" | "sessions"
> = {
  overview: "overview",
  "mcp-servers": "mcpServers",
  sessions: "sessions",
};

export default function RemoteSessionClientDetail(): JSX.Element {
  const { issuerId = "", clientId = "" } = useParams<{
    issuerId: string;
    clientId: string;
  }>();
  const routes = useRoutes();
  const location = useLocation();
  const {
    data: client,
    isLoading: isClientLoading,
    isError: isClientError,
  } = useOrganizationRemoteSessionClient({
    id: clientId,
  });
  const { data: issuer, isLoading: isIssuerLoading } =
    useOrganizationRemoteSessionIssuer({ id: issuerId });
  const queryClient = useQueryClient();
  // The organization update endpoint requires org:admin, which a platform
  // admin browsing with org:read alone does not have.
  const { hasAnyScope } = useRBAC();
  const migrate = useUpdateOrganizationRemoteSessionClientMutation({
    onSuccess: async () => {
      await invalidateAllOrganizationRemoteSessionClient(queryClient, {
        refetchType: "all",
      });
      toast.success("Client migrated to the new callback URL");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to migrate client",
      );
    },
  });

  const activeTab = activeDetailTab(location.pathname, CLIENT_TABS);
  const tabHref = (tab: ClientTab) =>
    routes.remoteIdentityProviders.clientDetail[CLIENT_TAB_ROUTE_KEY[tab]].href(
      issuerId,
      clientId,
    );

  const label = client
    ? remoteSessionClientDisplayName(client)
    : "Remote Session Client";
  // Mirror the issuer's own breadcrumb: its display name (name, falling back to
  // the issuer URL), and the raw id only while the issuer is still loading.
  const issuerLabel = issuer ? issuerDisplayName(issuer) : issuerId;

  // The client doesn't exist (or failed to load); return to the issuer's
  // Clients tab.
  if (isClientError || (!isClientLoading && !client)) {
    return (
      <Navigate
        to={routes.remoteIdentityProviders.issuerDetail.clients.href(issuerId)}
        replace
      />
    );
  }

  // The bare /:clientId URL has no tab, and the retired /settings tab merged
  // into Overview; canonicalize both to the Overview tab.
  if (!activeTab) {
    return <Navigate to={tabHref("overview")} replace />;
  }

  return (
    <Page>
      <Page.Header>
        <Page.Header.Breadcrumbs
          substitutions={{ [issuerId]: issuerLabel, [clientId]: label }}
        />
      </Page.Header>
      <Page.Body fullWidth noPadding className="gap-0">
        <DetailHero>
          <Page.Eyebrow />
          <div className="flex items-center gap-3">
            <Text small muted>
              Remote Session Client
            </Text>
            {client && (
              <ScopeBadge
                projectId={client.projectId}
                organizationId={client.organizationId}
              />
            )}
            {client?.credentialOwner === "self" && (
              <Badge variant="information" size="sm">
                <Badge.LeftIcon>
                  <Bot />
                </Badge.LeftIcon>
                <Badge.Text>Service account</Badge.Text>
              </Badge>
            )}
          </div>
          <Heading variant="h1" className="break-all normal-case">
            {label}
          </Heading>
        </DetailHero>

        <RequireScope scope={["org:read", "org:admin"]} level="page">
          <Tabs value={activeTab} className="flex w-full flex-1 flex-col">
            <div className="shrink-0 border-b">
              <div className="mx-auto max-w-[1270px] px-8">
                <PageTabsList className="h-auto gap-6 bg-transparent p-0">
                  <PageTabsTrigger value="overview" asChild>
                    <Link to={tabHref("overview")}>Overview</Link>
                  </PageTabsTrigger>
                  <PageTabsTrigger value="mcp-servers" asChild>
                    <Link to={tabHref("mcp-servers")}>MCP Servers</Link>
                  </PageTabsTrigger>
                  <PageTabsTrigger value="sessions" asChild>
                    <Link to={tabHref("sessions")}>Sessions</Link>
                  </PageTabsTrigger>
                </PageTabsList>
              </div>
            </div>

            <div className="mx-auto w-full max-w-[1270px] px-8 py-8">
              {client && (
                <LegacyCallbackAlert
                  legacyCallbackUrl={client.legacyCallbackUrl}
                  callbackUrl={client.callbackUrl}
                  onMigrate={() =>
                    migrate.mutate({
                      request: {
                        updateRemoteSessionClientForm: {
                          id: client.id,
                          legacyCallbackUrl: false,
                        },
                      },
                    })
                  }
                  isMigrating={migrate.isPending}
                  canMigrate={hasAnyScope(["org:admin"])}
                  className="mb-6 max-w-3xl"
                />
              )}
              <TabsContent value="overview" className="mt-0">
                {client && (
                  <OverviewTab
                    key={client.id}
                    client={client}
                    issuer={issuer}
                    isIssuerLoading={isIssuerLoading}
                    issuerId={issuerId}
                  />
                )}
              </TabsContent>
              <TabsContent value="mcp-servers" className="mt-0">
                <McpServersTab clientId={clientId} />
              </TabsContent>
              <TabsContent value="sessions" className="mt-0">
                <SessionsTab clientId={clientId} />
              </TabsContent>
            </div>
          </Tabs>
        </RequireScope>
      </Page.Body>
    </Page>
  );
}
